/**
 * PriceDisplay Component
 *
 * Displays current price of an investment with formatting and metadata
 * Shows price source, last updated time, and loading/error states
 */

import React from 'react';
import { formatCurrency, formatLastUpdated, isPriceStale } from '../utils/priceCalculations';
import { Loader2, TrendingUp, AlertCircle } from 'lucide-react';

/**
 * PriceDisplay Component
 *
 * @param {Object} props
 * @param {Object} props.priceData - Price data object from usePricing hook
 * @param {number} props.priceData.price - Current price
 * @param {string} props.priceData.currency - Currency code
 * @param {number} props.priceData.timestamp - Last update timestamp
 * @param {string} props.priceData.source - Data source name
 * @param {boolean} props.loading - Loading state
 * @param {Error} props.error - Error object
 * @param {boolean} props.showSource - Show data source (default: false)
 * @param {boolean} props.showLastUpdated - Show last updated time (default: true)
 * @param {string} props.size - Size variant ('sm', 'md', 'lg') (default: 'md')
 * @param {string} props.className - Additional CSS classes
 */
export const PriceDisplay = ({
  priceData,
  loading = false,
  error = null,
  showSource = false,
  showLastUpdated = true,
  size = 'md',
  className = ''
}) => {
  // Size variants
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  };

  const priceClasses = {
    sm: 'text-lg font-semibold',
    md: 'text-xl font-semibold',
    lg: 'text-2xl font-bold'
  };

  // Loading state
  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${sizeClasses[size]} ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        <span className="text-gray-500 dark:text-gray-400">Loading price...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`flex items-center gap-2 ${sizeClasses[size]} ${className}`}>
        <AlertCircle className="h-4 w-4 text-red-500" />
        <span className="text-red-600 dark:text-red-400">Price unavailable</span>
      </div>
    );
  }

  // No price data
  if (!priceData || !priceData.price) {
    return (
      <div className={`flex items-center gap-2 ${sizeClasses[size]} ${className}`}>
        <AlertCircle className="h-4 w-4 text-yellow-500" />
        <span className="text-gray-500 dark:text-gray-400">No price data</span>
      </div>
    );
  }

  const { price, currency, timestamp, source } = priceData;
  const isStale = isPriceStale(timestamp);

  return (
    <div className={`space-y-1 ${className}`}>
      {/* Price */}
      <div className="flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-blue-500" />
        <span className={`${priceClasses[size]} text-gray-900 dark:text-gray-100`}>
          {formatCurrency(price, currency, 2)}
        </span>
        {isStale && (
          <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded">
            Stale
          </span>
        )}
      </div>

      {/* Metadata */}
      <div className={`flex items-center gap-3 ${sizeClasses[size]} text-gray-500 dark:text-gray-400`}>
        {showLastUpdated && timestamp && (
          <span className="flex items-center gap-1">
            <span className="text-xs">Updated:</span>
            <span className="text-xs font-medium">{formatLastUpdated(timestamp)}</span>
          </span>
        )}

        {showSource && source && (
          <span className="flex items-center gap-1">
            <span className="text-xs">•</span>
            <span className="text-xs capitalize">{source.replace(/_/g, ' ')}</span>
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * CompactPriceDisplay Component
 * Minimal inline price display
 *
 * @param {Object} props
 * @param {number} props.price - Current price
 * @param {string} props.currency - Currency code
 * @param {string} props.className - Additional CSS classes
 */
export const CompactPriceDisplay = ({ price, currency = 'EUR', className = '' }) => {
  if (!price) {
    return <span className={`text-gray-400 ${className}`}>-</span>;
  }

  return (
    <span className={`font-semibold text-gray-900 dark:text-gray-100 ${className}`}>
      {formatCurrency(price, currency, 2)}
    </span>
  );
};

export default PriceDisplay;
